import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { LucideProvider } from 'lucide-react';
import { Provider } from 'react-redux';
import { store } from './store';

// Shell
import Layout from './components/layout/Layout';
import RequireAuth from './components/auth/RequireAuth';
import RequireRole from './components/auth/RequireRole';
import { ThemeColorSync } from './components/layout/ThemeModeMenu';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Pages
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import EmployeeDetail from './pages/EmployeeDetail';
import Departments from './pages/Departments';
import TimeOff from './pages/TimeOff';
import TimeOffDetail from './pages/TimeOffDetail';
import DepartmentDetail from './pages/DepartmentDetail';
import ImportData from './pages/ImportData';
import HRTimeOffManager from './pages/HRTimeOffManager';
import AdminPanel from './pages/Admin/AdminPanel';
import EmailDashboard from './pages/Admin/EmailDashboard';
import FloorPlan from './pages/Admin/FloorPlan';
import OrgChartPage from './pages/Admin/OrgChart';
import WeekendShiftManager from './pages/Admin/WeekendShiftManager';
import SalesOfficeCheckinManager from './pages/Admin/SalesOfficeCheckinManager';
import TeamAttendance from './pages/TeamAttendance';
import Analytics from './pages/Analytics';
import Login from './pages/Auth/Login';
import ResetPassword from './pages/Auth/ResetPassword';
import NotFound from './pages/NotFound';

import { theme } from './theme';
import { AuthRole, MANAGER_LIKE_ROLES } from './utils/roles';

// Who may open each route. These lists are the access rules; keep them in
// step with the sidebar (components/layout/Sidebar.tsx).
const ADMIN_HR: AuthRole[] = ['admin', 'hr'];
const ADMIN_HR_MANAGERS: AuthRole[] = ['admin', 'hr', ...MANAGER_LIKE_ROLES];
const EVERYONE: AuthRole[] = ['admin', 'hr', ...MANAGER_LIKE_ROLES, 'employee'];

const guard = (roles: AuthRole[], element: React.ReactNode) => <RequireRole roles={roles}>{element}</RequireRole>;

const DefaultRedirect: React.FC = () => {
  const { profile } = useAuth();
  return <Navigate to={profile?.role === 'employee' ? '/org-chart' : '/dashboard'} replace />;
};

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme} defaultMode="light" disableTransitionOnChange>
        <CssBaseline />
        <ThemeColorSync />
        <LucideProvider strokeWidth={1.75} size={18}>
          <AuthProvider>
            <Router>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/"
                  element={
                    <RequireAuth>
                      <Layout />
                    </RequireAuth>
                  }
                >
                  <Route index element={<DefaultRedirect />} />

                  {/* Workspace */}
                  <Route path="dashboard" element={guard(ADMIN_HR_MANAGERS, <Dashboard />)} />
                  <Route path="employees" element={guard(ADMIN_HR, <Employees />)} />
                  <Route path="employees/:id" element={guard(ADMIN_HR_MANAGERS, <EmployeeDetail />)} />
                  <Route path="departments" element={guard(ADMIN_HR, <Departments />)} />
                  <Route path="departments/:id" element={guard(['admin', 'hr', 'manager'], <DepartmentDetail />)} />
                  <Route path="org-chart" element={guard(EVERYONE, <OrgChartPage />)} />
                  <Route path="admin/floorplan" element={guard(['admin', ...MANAGER_LIKE_ROLES, 'employee'], <FloorPlan />)} />

                  {/* Attendance */}
                  <Route path="time-off" element={guard(ADMIN_HR_MANAGERS, <TimeOff />)} />
                  <Route path="time-off/:id" element={guard(ADMIN_HR_MANAGERS, <TimeOffDetail />)} />
                  <Route path="hr-time-off" element={guard(ADMIN_HR, <HRTimeOffManager />)} />
                  <Route path="hr-time-off/calendar" element={guard(ADMIN_HR_MANAGERS, <HRTimeOffManager />)} />
                  <Route path="team-attendance" element={guard(['admin', ...MANAGER_LIKE_ROLES], <TeamAttendance />)} />
                  <Route path="admin/weekend-shifts" element={guard(['admin'], <WeekendShiftManager />)} />
                  <Route path="admin/sales-office-checkin" element={guard(['admin'], <SalesOfficeCheckinManager />)} />

                  {/* Insights */}
                  <Route path="analytics" element={guard(ADMIN_HR, <Analytics />)} />
                  <Route path="admin/emails" element={guard(ADMIN_HR, <EmailDashboard />)} />

                  {/* Tools */}
                  <Route path="import" element={guard(ADMIN_HR, <ImportData />)} />
                  <Route path="admin" element={guard(ADMIN_HR, <AdminPanel />)} />
                  <Route path="admin/calendar" element={guard(ADMIN_HR, <Navigate to="/admin?tab=calendar" replace />)} />

                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </Router>
          </AuthProvider>
        </LucideProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
